import React from 'react';
import PropTypes from 'prop-types';

import {Box, Card, Stack, Typography, TextField, InputAdornment, IconButton, CircularProgress} from 'views/components';
import {ArrowCircleRightRounded, AutoAwesome} from 'views/icons';
import {Translate, translate} from 'views/module';

const AiQuestionCard = (props) => {
  const {clientRn, page} = props;

  const [query, setQuery] = React.useState('');
  const [answered, setAnswered] = React.useState(false);
  const [response, setResponse] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const caregiverDurationSuggestion = translate('aiQuestionCard.suggestions.caregiverDuration');
  const earliestStartDaySuggestion = translate('aiQuestionCard.suggestions.earliestStartDay');
  const requestErrorResponse = translate('aiQuestionCard.response.requestError');

  const onSubmit = async (submittedQuery) => {
    if (!submittedQuery || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setAnswered(false);

    try {
      const helpMessageApi = `${process.env.REACT_APP_ONBOARDING_API}/help_message`;
      const result = await fetch(
          helpMessageApi,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              message: submittedQuery,
              clientRn,
              page,
            }),
          },
      );

      const data = await result.json();
      const output = data?.message;
      setResponse(output);
    } catch (error) {
      console.error('Error submitting help message:', error);
      setResponse(requestErrorResponse);
    } finally {
      setAnswered(true);
      setIsSubmitting(false);
    }
  };

  const onSelectSuggestion = (suggestion) => {
    setQuery(suggestion);
    void onSubmit(suggestion);
  };

  return (
    <Card>
      <Stack sx={{gap: '16px', p: '24px'}}>
        <Box sx={{gap: '8px', display: 'flex', alignItems: 'center'}}>
          <AutoAwesome sx={{fontSize: '20px', color: '#E2C235'}}/>
          <Typography sx={{fontWeight: 400, fontSize: '16px', color: '#000000', lineHeight: '150%'}}>
            <Translate expression='aiQuestionCard.title'/>
          </Typography>
        </Box>

        <Box>
          <TextField
            type="text"
            placeholder={translate('aiQuestionCard.placeholder')}
            fullWidth
            value={query}
            onChange={setQuery}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                void onSubmit(query);
              }
            }}
            sx={{'& .MuiInputBase-root':
                {
                  borderRadius: '12px',
                  px: '12px',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '150%',
                },
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      disabled={isSubmitting}
                      onClick={() => {
                        void onSubmit(query);
                      }}
                    >
                      {
                        isSubmitting ?
                          <CircularProgress size={24} sx={{color: '#DA7955'}}/> :
                          <ArrowCircleRightRounded sx={{fontSize: '32px', color: '#DA7955'}}/>
                      }
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        {
          !answered && (
            <Stack direction='row' sx={{gap: '12px'}}>
              <Box
                onClick={() => onSelectSuggestion(caregiverDurationSuggestion)}
                sx={{borderRadius: '20px', py: '8px', px: '18px', border: '1px solid #DED8D7', cursor: 'pointer'}}
              >
                <Typography sx={{fontWeight: 500, fontSize: '14px', lineHeight: '150%', color: '#2D2925'}}>
                  <Translate expression='aiQuestionCard.suggestions.caregiverDuration'/>
                </Typography>
              </Box>
              <Box
                onClick={() => onSelectSuggestion(earliestStartDaySuggestion)}
                sx={{borderRadius: '20px', py: '8px', px: '18px', border: '1px solid #DED8D7', cursor: 'pointer'}}
              >
                <Typography sx={{fontWeight: 500, fontSize: '14px', lineHeight: '150%', color: '#2D2925'}}>
                  <Translate expression='aiQuestionCard.suggestions.earliestStartDay'/>
                </Typography>
              </Box>
            </Stack>
          )
        }
        {
          answered && (
            <Box>
              <Typography sx={{fontWeight: 400, fontSize: '16px', lineHeight: '150%', color: '#3C3C3C'}}>
                {response}
              </Typography>
            </Box>
          )
        }
      </Stack>
    </Card>
  );
};

AiQuestionCard.propTypes = {
  clientRn: PropTypes.string,
  page: PropTypes.string,
};

export default React.memo(AiQuestionCard);
